import { useState, useCallback, useEffect, useRef } from 'react';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { AnyNetworkElement, NetworkElementType, Abgangsleitung, Hausanschluss, CONNECTION_KIND_LABELS, connectionKindOf } from '../../types/network';
import { elementDisplayName } from '../../utils/elementDisplayName';
import { SearchIcon, searchInputClass } from './searchStyles';

const ELEMENT_TYPE_LABELS: Record<string, string> = {
  trafostation: 'Trafostation',
  kabelverteilschrank: 'Kabelverteilschrank',
  hausanschluss: 'Hausanschluss',
  abgangsleitung: 'Kabel',
  muffe: 'Muffe',
};

/**
 * Header search over the network elements of the active zone. Shown only on tabs that show
 * the zone on a map (Karte, Stressszenarios, Netzanschlussprüfung) — picking a result selects
 * the element and jumps the map to it.
 */
export function ElementSearch() {
  const isRightSidebarCollapsed = useNetworkUIStore(state => state.isRightSidebarCollapsed);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<NetworkElementType | 'all'>('all');
  const [showResults, setShowResults] = useState(false);

  const elements = useNetworkElementsStore(state => state.elements);
  const setMapView = useNetworkUIStore(s => s.setMapView);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const setHighlightedElement = useNetworkUIStore(s => s.setHighlightedElement);
  const [searchResults, setLocalSearchResults] = useState<AnyNetworkElement[]>([]);

  // Live search while typing is debounced; Enter/Lupe run immediately and must cancel the
  // pending debounced run - otherwise it re-opened the dropdown (with its click-blocking
  // backdrop) right after the user had picked a result.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback((term: string) => {
    clearTimeout(searchDebounceRef.current);
    setSearchTerm(term);

    if (!term.trim()) {
      setLocalSearchResults([]);
      setShowResults(false);
      return;
    }

    const searchLower = term.toLowerCase();
    const filteredByType = selectedType === 'all'
      ? elements
      : elements.filter(element => element.type === selectedType);

    // Search the same name the map/sidebar show, so a station-derived cable label is
    // findable too, next to raw name/id (auto ids stay searchable).
    const resolve = (id: string) => elements.find(e => e.id === id);
    const results = filteredByType.filter(element =>
      element.id.toLowerCase().includes(searchLower) ||
      element.name.toLowerCase().includes(searchLower) ||
      elementDisplayName(element, resolve).toLowerCase().includes(searchLower) ||
      element.index.toString() === searchLower
    ).slice(0, 10);

    setLocalSearchResults(results);
    setShowResults(true);
  }, [elements, selectedType]);

  // Live search while typing (also fires on paste); Enter/Lupe stay as instant triggers.
  const handleSearchInput = useCallback((term: string) => {
    setSearchTerm(term);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => handleSearch(term), 150);
  }, [handleSearch]);
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const handleElementSelect = useCallback((element: AnyNetworkElement) => {
    setSelectedElement(element);
    setShowResults(false);

    if (element.type === 'abgangsleitung') {
      const abgangsleitung = element as Abgangsleitung;
      if (abgangsleitung.waypoints && abgangsleitung.waypoints.length > 0) {
        const centerPoint = abgangsleitung.waypoints[Math.floor(abgangsleitung.waypoints.length / 2)];
        setMapView({ lat: centerPoint.lat, lng: centerPoint.lng }, 20);
      } else {
        setMapView(element.coordinates, 20);
      }
    } else if ('coordinates' in element) {
      setMapView(element.coordinates, 20);
    }

    setHighlightedElement(element.id);
    setTimeout(() => setHighlightedElement(null), 3000);
  }, [setSelectedElement, setMapView, setHighlightedElement]);

  return (
    <div className={`shrink-0 relative border-l border-gray-300 dark:border-primary-600 px-2 transition-all duration-300 ${isRightSidebarCollapsed ? 'w-auto' : 'w-96'}`}>
      <div className="flex gap-1.5 items-center">
        <select
          value={selectedType}
          onChange={(e) => {
            setSelectedType(e.target.value as NetworkElementType | 'all');
            if (searchTerm.trim()) handleSearch(searchTerm);
          }}
          className="w-24 shrink-0 px-1.5 py-[5px] bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 text-gray-900 dark:text-primary-100 rounded focus:outline-hidden focus:ring-1 focus:ring-accent-primary text-xs"
          aria-label="Nach Elementtyp filtern"
        >
          <option value="all">Alle Typen</option>
          <option value="trafostation">Trafo</option>
          <option value="abgangsleitung">Leitung</option>
          <option value="kabelverteilschrank">KVS</option>
          <option value="hausanschluss">HA</option>
          <option value="muffe">Muffe</option>
        </select>

        <input
          type="text"
          placeholder="Netzelement suchen…"
          value={searchTerm}
          onChange={(e) => handleSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch(searchTerm);
            if (e.key === 'Escape') setShowResults(false);
          }}
          onFocus={() => { if (searchTerm.trim()) setShowResults(true); }}
          className={`flex-1 min-w-0 ${searchInputClass}`}
          aria-label="Netzelemente suchen"
        />

        <button
          onClick={() => handleSearch(searchTerm)}
          className="p-[7px] bg-accent-primary hover:bg-accent-primary/90 text-white rounded transition-colors"
          title="Suchen"
        >
          <SearchIcon />
        </button>
      </div>

      {showResults && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowResults(false)} />
          <div className="absolute right-0 top-full mt-1 w-80 max-h-60 overflow-y-auto bg-white dark:bg-primary-800 border border-gray-300 dark:border-primary-500 rounded-lg shadow-xl z-50">
            {searchResults.length === 0 && (
              <div className="p-3 text-xs text-gray-500 dark:text-primary-300">
                Keine Treffer für „{searchTerm.trim()}"
                {selectedType !== 'all' && ' im gewählten Elementtyp'}
              </div>
            )}
            {searchResults.map(element => (
              <div
                key={element.id}
                onClick={() => handleElementSelect(element)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-primary-600 cursor-pointer transition-colors duration-150 border-b border-gray-200 dark:border-primary-600 last:border-b-0"
                role="button"
                tabIndex={0}
              >
                <div className="font-medium text-sm text-gray-900 dark:text-primary-100" title={element.name}>
                  {elementDisplayName(element, id => elements.find(e => e.id === id))}
                </div>
                <div className="text-xs text-gray-600 dark:text-primary-300">
                  {ELEMENT_TYPE_LABELS[element.type] ?? element.type}
                  {element.type === 'hausanschluss' && connectionKindOf(element as Hausanschluss) !== 'household'
                    ? ` · ${CONNECTION_KIND_LABELS[connectionKindOf(element as Hausanschluss)]}`
                    : ''}
                  {' · ID: '}{element.id}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
